import { OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import * as sortBy from 'lodash/sortBy';
import { ChangeType, TerminalProcedure, TerminalProcedureBatchFormat, TerminalProcedureCycle } from '../../airport/models/terminal-procedure.model';
import { AirportService, MAX_PROC_BATCH_FILESIZE, MAX_PROC_BATCH_LIMIT } from '../../airport/airport.service';
import { StatefulPromise } from '../../util/stateful-promise';
import { formatBytes } from '../../util/helpers';
/**
 * Error message shown when no procedures are selected.
 */
var NO_PROCS_ERROR = 'Please select at least one procedure to download.';
/**
 * Error message shown when the total size of selected procedures is over the limit.
 */
var SIZE_LIMIT_ERROR = 'Downloads over 25 MB do not complete reliably and are currently disabled. ' +
    'Please select fewer procedures to download.';
/**
 * Error message shown when too many procedures are selected.
 */
var PROC_LIMIT_ERROR = "Please select no more than " + MAX_PROC_BATCH_LIMIT + " procedures to download at a time.";
/**
 * Placeholder message to show when no terminal procedures are available yet.
 */
var NO_PROCS_AVAILABLE = 'Terminal procedures are not yet available for the selected data cycle. Please check back later.';
/**
 * Placeholder message to show when no terminal procedures have changed in the selected data cycle.
 */
var NO_PROCS_CHANGED = 'No terminal procedures have been changed in the selected data cycle.';
var ProcDownloadComponent = /** @class */ (function () {
    function ProcDownloadComponent(dialogRef, data, _airportService) {
        this.dialogRef = dialogRef;
        this.data = data;
        this._airportService = _airportService;
        this.TerminalProcedureBatchFormat = TerminalProcedureBatchFormat;
        this.TerminalProcedureCycle = TerminalProcedureCycle;
        this.format = TerminalProcedureBatchFormat.PDF;
        this.selectedProcs = null;
        this.numSelected = null;
        this.totalSize = null;
        this.totalSizeStr = null;
        this.downloadDisabled = true;
        this.message = null;
        this._cycle = TerminalProcedureCycle.Current;
        this._changedOnly = false;
        this._procedurePack = null;
        this._procedureSet = null;
        this._allProcedures = [];
        this._changedProcedures = [];
        this._procedures = [];
        this._deletedProcedures = [];
        this._effectiveDate = null;
        if (data) {
            this.airports = data.airports;
        }
    }
    Object.defineProperty(ProcDownloadComponent.prototype, "cycle", {
        get: function () {
            return this._cycle;
        },
        set: function (value) {
            this._cycle = value;
            this._updateProcedureSet();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProcDownloadComponent.prototype, "changedOnly", {
        get: function () {
            return this._changedOnly;
        },
        set: function (value) {
            this._changedOnly = value;
            this._updateProcedureSet();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProcDownloadComponent.prototype, "procedureData", {
        get: function () {
            return this._procedurePack;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProcDownloadComponent.prototype, "procedures", {
        get: function () {
            return this._procedures;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProcDownloadComponent.prototype, "effectiveDate", {
        get: function () {
            return this._effectiveDate;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProcDownloadComponent.prototype, "placeholderMessage", {
        get: function () {
            if (this._allProcedures.length === 0) {
                return NO_PROCS_AVAILABLE;
            }
            if (this._changedProcedures.length === 0) {
                return NO_PROCS_CHANGED;
            }
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProcDownloadComponent.prototype, "deletedProcedures", {
        /**
         * Returns terminal procedures that have been deleted since the last data cycle
         * @returns {Array<TerminalProcedure>}
         */
        get: function () {
            return this._deletedProcedures;
        },
        enumerable: true,
        configurable: true
    });
    ProcDownloadComponent.prototype.ngOnInit = function () {
        var _this = this;
        var ids = this.airports.map(function (a) { return a.faaId; });
        var promise = this._airportService.getTerminalProcedures(ids, true);
        this.procsPromise = new StatefulPromise(promise);
        promise.then(function (p) { return _this.onProceduresLoaded(p); });
    };
    ProcDownloadComponent.prototype.onProceduresLoaded = function (data) {
        this._procedurePack = data;
        this._updateProcedureSet();
    };
    ProcDownloadComponent.prototype._updateProcedureSet = function () {
        this._procedureSet = this._procedurePack[this._cycle];
        this._allProcedures = this._procedureSet.procedures;
        this._changedProcedures = this._allProcedures
            .filter(function (p) { return p.changeType === ChangeType.Added || p.changeType === ChangeType.Changed; });
        this._deletedProcedures = this._allProcedures.filter(function (p) { return p.changeType === ChangeType.Deleted; });
        this._deletedProcedures = sortBy(this._deletedProcedures, [
            'airportId',
            function (p) { return TerminalProcedure.formatProcedureType(p.type); },
            'name'
        ]);
        this._procedures = this._changedOnly ? this._changedProcedures : this._allProcedures;
        this._effectiveDate = this._procedureSet.effDateFrom;
        // Reset selected procedures. If there is at least one procedure available, this will be updated after the
        // proc-selection-tree component is initialized and it emits a selection changed event. Otherwise, these settings
        // will remain in effect, and a placeholder message will be shown in place of the proc-selection-tree component (and
        // the download button will remain disabled).
        this.selectedProcs = [];
        this.numSelected = null;
        this.message = null;
        this.downloadDisabled = true;
    };
    ProcDownloadComponent.prototype.getAirportIdentifier = function (airportId) {
        var airport = this.airports.find(function (a) { return a.airportId === airportId; });
        return airport ? airport.identifier : null;
    };
    ProcDownloadComponent.prototype.formatProcType = function (type) {
        return TerminalProcedure.formatProcedureType(type);
    };
    ProcDownloadComponent.prototype.onSelectionChanged = function (e) {
        var _this = this;
        setTimeout(function () {
            _this.selectedProcs = e.selectedProcs;
            _this.numSelected = e.selectedFiles.size;
            _this.totalSize = e.totalSize;
            _this.totalSizeStr = formatBytes(e.totalSize);
            _this.validate();
        }, 0);
    };
    ProcDownloadComponent.prototype.validate = function () {
        this.message = null;
        this.downloadDisabled = false;
        if (this.numSelected === 0) {
            this.message = NO_PROCS_ERROR;
            this.downloadDisabled = true;
        }
        if (this.numSelected > MAX_PROC_BATCH_LIMIT) {
            this.message = PROC_LIMIT_ERROR;
            this.downloadDisabled = true;
        }
        if (this.totalSize > MAX_PROC_BATCH_FILESIZE) {
            this.message = SIZE_LIMIT_ERROR;
            this.downloadDisabled = true;
        }
    };
    ProcDownloadComponent.prototype.download = function () {
        var url = this._airportService.getProcedureBatchUrl(this.selectedProcs, this.format);
        window.open(url);
    };
    ProcDownloadComponent.prototype.closeDialog = function () {
        this.dialogRef.close();
    };
    return ProcDownloadComponent;
}());
export { ProcDownloadComponent };
